import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";

const FAQS = [
  {
    q: "What is Carivisor?",
    a: "Carivisor is a Caribbean tours, activities and experiences booking platform. It connects travelers with excursions offered by local tour operators across the Caribbean — from ATV adventures and boat trips to snorkeling, sightseeing, food tours and family-friendly activities — all bookable in one place.",
  },
  {
    q: "Which Caribbean destinations does Carivisor cover?",
    a: "Carivisor's journey begins with Grenada, St. Vincent and the Grenadines, and Barbados, with a vision of expanding across the wider Caribbean. As we grow, you'll be able to discover and book experiences on more islands from one Caribbean-focused platform.",
  },
  {
    q: "What kinds of experiences can I book?",
    a: "You'll find ATV and off-road adventures, island sightseeing tours, boat tours and sailing, snorkeling and water activities, beach experiences, private tours, airport and transportation, food and cultural tours, historical and heritage attractions, family activities, romantic experiences, group excursions, cruise shore excursions, adventure tours, and nature and eco experiences.",
  },
  {
    q: "Can I book a tour before I arrive on the island?",
    a: "Yes. Carivisor is designed for both planning ahead and booking on the go. You can research and reserve experiences before your trip to lock in your plans, or find something to do while you're already on the island.",
  },
  {
    q: "How do I book an experience?",
    a: "Search by destination or category, compare experiences and see what's included, then choose your date, time and number of guests and book. Booking is quick and secure — and with saved payment details you can complete future bookings in one tap.",
  },
  {
    q: "Are the tours run by local operators?",
    a: "Yes. Carivisor is a marketplace for local guides, excursion companies, attractions and independent operators. Booking through Carivisor helps direct more of the tourism economy to the Caribbean businesses and communities that create these experiences.",
  },
  {
    q: "I'm arriving on a cruise ship — can Carivisor help?",
    a: "Absolutely. Carivisor lists cruise shore excursions and shorter experiences ideal for the hours you have ashore, so you can make the most of your port day without the guesswork.",
  },
  {
    q: "How does payment work? Is it secure?",
    a: "Payments are processed securely through Stripe. You can save a card for faster one-tap bookings on your future trips, and you'll always see the full price and what's included before you confirm.",
  },
  {
    q: "Do I earn any rewards for booking?",
    a: "Yes. Carivisor travelers earn loyalty points on eligible bookings that can be redeemed toward future excursions, so exploring more of the Caribbean is even more rewarding.",
  },
  {
    q: "How do I check in for my booked experience?",
    a: "After booking you'll receive a confirmation with a QR check-in code. Simply show the code to your operator at the meeting point to check in — no printing required.",
  },
  {
    q: "Can I cancel or change a booking?",
    a: "You can manage your trips from your traveler dashboard. Cancellation and change options depend on the individual experience and its policy, which is shown on the listing before you book.",
  },
  {
    q: "I run a tour company — how do I list my experiences?",
    a: "Create a provider account to join Carivisor as a partner. You can showcase your experiences, set your pricing and connect with travelers from around the world who are actively searching for things to do in the Caribbean.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#060810]">
      <Seo
        title="Carivisor FAQ | Caribbean Tours, Excursions & Booking Questions"
        description="Answers to the most common questions about booking Caribbean tours, excursions and activities with Carivisor — destinations, payments, cruise shore excursions, rewards, check-in and more."
        keywords="Caribbean tours FAQ, Caribbean excursions questions, how to book Caribbean tours, things to do in the Caribbean, Grenada tours, Barbados tours, St. Vincent tours, cruise shore excursions"
        path="/faq"
        jsonLd={jsonLd}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-4">
            <HelpCircle className="w-4 h-4" /> Frequently Asked Questions
          </div>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-tight">
            Your questions, <span className="text-gradient-warm">answered</span>
          </h1>
          <p className="text-slate-400 mt-4">Everything you need to know about discovering and booking Caribbean experiences with Carivisor.</p>
        </div>

        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="glass rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-5"
                >
                  <span className="font-heading font-bold text-white text-base sm:text-lg">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-cyan-400 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p data-testid={`faq-answer-${i}`} className="px-5 pb-5 text-slate-300 text-sm leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="glass rounded-2xl p-8 mt-12 text-center">
          <h2 className="font-heading font-bold text-white text-2xl mb-2">Still have questions?</h2>
          <p className="text-slate-400 text-sm mb-6">Explore experiences or learn more about how Carivisor works.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/browse" className="px-6 py-3 rounded-full btn-neon font-semibold flex items-center gap-2">
              Browse experiences <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about" className="px-6 py-3 rounded-full glass text-slate-200 hover:text-cyan-400 font-semibold">
              About Carivisor
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
